/**
 * Pure color helpers and fallback constants behind the runtime branding palette
 * (ThemeStore.setColors). Extracted from theme.store.ts so the Storybook preview
 * (.storybook/preview.ts) can mirror the exact app palette without importing the store itself —
 * its import chain drags in stores/services that cannot boot outside the app. Single source of
 * truth: both theme.store.ts and .storybook/preview.ts import from here, so story shades and
 * fallback colors can never drift from the app.
 */

/**
 * Accent is not brandable: the interface dictates it so subtle text/icons stay
 * consistent across tenants. Hardcoded per scheme (see branding settings form,
 * which intentionally has no accent picker).
 *
 * The same two values live statically as `--dd-accent` in src/styles/components/_forms.scss
 * (fields cannot depend on --fuse-accent: theming.js pre-defines it as gray-400 before any
 * branding is saved). Keep both pairs in sync when the design value changes.
 */
export const ACCENT_COLOR = { light: '#545559', dark: '#c7c7cc' } as const;

/** Fallback for tenants that saved branding before the "Waarschuwing" field existed.
 *  Figma Foreground/Warning/Primary per scheme (Amber/700, Amber/500). */
export const DEFAULT_WARNING_COLOR = { light: '#8A6000', dark: '#FFB500' } as const;

/** Fallbacks for tenants that saved branding before the "Secondary"/"Success" fields existed. */
export const DEFAULT_SECONDARY_COLOR = { light: '#545559', dark: '#C7C7CC' } as const;
/** Figma Positive foreground per scheme. */
export const DEFAULT_SUCCESS_COLOR = { light: '#007A4C', dark: '#7DEFC3' } as const;

export function getContrastYIQ(hexcolor: string): string {
  const { r, g, b } = hexToRgb(hexcolor);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000000' : '#ffffff';
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let r = 0,
    g = 0,
    b = 0;

  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  return { r, g, b };
}

export function rgbToString(rgb: { r: number; g: number; b: number }): string {
  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}

/** Which element a branding variable belongs on — the split is policy, not preference. */
export type BrandingVarTarget = 'body' | 'root';

export interface BrandingVar {
  target: BrandingVarTarget;
  name: string;
  value: string;
}

/**
 * Every CSS custom property the runtime branding writes for ONE colour channel, with the element
 * each one belongs on. This is the write policy — variable names, the body/root split and the
 * shade-600 quirk below — and it is deliberately the single source for both writers:
 * ThemeStore.setColors in the app, and applyBranding in .storybook/preview.ts. They used to carry
 * line-for-line copies of this list, so any new variable silently reached only one of them.
 *
 * Pure on purpose: it returns what to write, never touches the DOM. The callers own the sink —
 * the store writes straight through, the preview also records each name so it can clear the
 * previous tenant's variables when the toolbar switches branding.
 */
export function brandingVars(color: string, hex: string): BrandingVar[] {
  const palette = generatePalette(hex);
  const vars: BrandingVar[] = [];
  const body = (name: string, value: string): void => void vars.push({ target: 'body', name, value });
  const root = (name: string, value: string): void => void vars.push({ target: 'root', name, value });

  for (const [shade, value] of Object.entries(palette)) {
    body(`--fuse-${color}-${shade}`, value);
    body(`--fuse-${color}-${shade}-rgb`, rgbToString(hexToRgb(value)));

    // Per-shade on-color, so Tailwind utilities like `text-on-primary-600` keep contrasting with
    // the runtime branding palette.
    const shadeContrast = getContrastYIQ(value);
    body(`--fuse-on-${color}-${shade}`, shadeContrast);
    body(`--fuse-on-${color}-${shade}-rgb`, rgbToString(hexToRgb(shadeContrast)));
  }

  body(`--fuse-${color}`, hex);
  // Parity over logic: the default -rgb is derived from shade 600, not from the base hex. Shade
  // 600 IS the configured colour (factor 0), so the two agree in practice — but the app has always
  // read it off the palette and the preview must keep matching that.
  body(`--fuse-${color}-rgb`, rgbToString(hexToRgb(palette[600])));

  // Contrast (on-color) drives --mat-sys-on-{primary|secondary|error} via _m3-consolidation.scss,
  // so filled/raised button text flips black/white automatically with the configured colour.
  const contrast = getContrastYIQ(hex);
  body(`--fuse-${color}-contrast`, contrast);
  body(`--fuse-${color}-contrast-rgb`, rgbToString(hexToRgb(contrast)));

  // DEFAULT on-color for Tailwind utilities (`text-on-primary` etc.), which resolve to
  // rgba(var(--fuse-on-{color}-rgb), <alpha>).
  body(`--fuse-on-${color}`, contrast);
  body(`--fuse-on-${color}-rgb`, rgbToString(hexToRgb(contrast)));

  root(`--fuse-${color}-button-text`, contrast);
  root(`--mat-theme-${color}`, hex);

  return vars;
}

function luminance(r: number, g: number, b: number, factor: number): string {
  const newR = Math.round(Math.min(Math.max(0, r + factor * (255 - r)), 255))
    .toString(16)
    .padStart(2, '0');
  const newG = Math.round(Math.min(Math.max(0, g + factor * (255 - g)), 255))
    .toString(16)
    .padStart(2, '0');
  const newB = Math.round(Math.min(Math.max(0, b + factor * (255 - b)), 255))
    .toString(16)
    .padStart(2, '0');
  return `#${newR}${newG}${newB}`;
}

export function generatePalette(hex: string): { [key: number]: string } {
  const rgb = hexToRgb(hex);
  const palette: { [key: number]: string } = {};

  // One factor per 50-step shade: shade = (index + 1) * 50, so index 11 (shade 600) is factor 0
  // — the base colour the branding was configured with. Negative darkens, positive lightens.
  // Laid out one per line with its shade because two entries used to be off by a factor of ten
  // (550 read -0.5 instead of -0.05, 650 read 0.5 instead of 0.05), which silently made 550 a
  // duplicate of 100 and 650 far lighter than 700. Both are unconsumed shades, so nothing in the
  // portal rendered differently — the generator was simply wrong.
  const factors = [
    -0.55, // 50
    -0.5, // 100
    -0.45, // 150
    -0.4, // 200
    -0.35, // 250
    -0.3, // 300
    -0.25, // 350
    -0.2, // 400
    -0.15, // 450
    -0.1, // 500
    -0.05, // 550
    0, // 600 — the configured brand colour itself
    0.05, // 650
    0.1, // 700
    0.15, // 750
    0.2, // 800
    0.25, // 850
    0.3, // 900
    0.35, // 950
  ];

  factors.forEach((factor, index) => {
    const shade = (index + 1) * 50;
    palette[shade] = luminance(rgb.r, rgb.g, rgb.b, factor);
  });

  return palette;
}
